import React from 'react';
import { ChevronDown, ChevronUp, BarChart3 } from 'lucide-react';

interface SizeOptimizationStepProps {
  expandedSection: string | null;
  toggleSection: (section: string) => void;
}

export const SizeOptimizationStep: React.FC<SizeOptimizationStepProps> = ({
  expandedSection,
  toggleSection,
}) => {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50">
        <div className="flex items-center justify-between cursor-pointer" onClick={() => toggleSection('size-opt')}>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-gray-100 text-gray-700 flex items-center justify-center text-sm font-semibold">4</div>
            <h3 className="text-base font-semibold text-black">Size Optimization</h3>
          </div>
          {expandedSection === 'size-opt' ? <ChevronUp className="w-4 h-4 text-gray-600" /> : <ChevronDown className="w-4 h-4 text-gray-600" />}
        </div>
      </div>
      {expandedSection === 'size-opt' && (
        <div className="p-6">
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              The algorithm tests multiple BESS configurations to find the optimal size that maximizes NPV
              while respecting technical and economic constraints.
            </p>

            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-xs font-semibold text-gray-700 mb-2">
                <BarChart3 className="w-3 h-3 inline mr-1" />
                OPTIMIZATION APPROACH
              </p>
              <pre className="text-xs bg-white p-3 rounded border border-gray-200 overflow-x-auto">
{`def optimize_size(power_range, energy_range):
    best_npv = -infinity
    best_config = None
    
    # Grid search with intelligent stepping
    for power_mw in range(power_range[0], power_range[1], step):
        for energy_mwh in range(energy_range[0], energy_range[1], step):
            # Ensure valid energy/power ratio (1-8 hours)
            if energy_mwh/power_mw < 1 or energy_mwh/power_mw > 8:
                continue
            
            # Simulate operation
            config = BESSConfig(power_mw, energy_mwh)
            results = simulate_operation(config)
            
            # Calculate economics
            capex = power_mw * 950000 + energy_mwh * 475000
            annual_opex = capex * 0.02
            
            # NPV calculation (15 years, 8% discount)
            npv = -capex
            for year in range(1, 16):
                degradation = (1 - 0.02) ** year
                inflation = (1 + 0.03) ** year
                net_cashflow = results.savings * degradation * inflation - annual_opex
                npv += net_cashflow / (1.08 ** year)
            
            if npv > best_npv:
                best_npv = npv
                best_config = config
    
    return best_config`}</pre>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-xs font-semibold text-gray-700 mb-2">
                Test Results (Top 5 Configurations)
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-1">Size (MW/MWh)</th>
                      <th className="text-right py-1">Annual Savings</th>
                      <th className="text-right py-1">CAPEX</th>
                      <th className="text-right py-1">NPV</th>
                      <th className="text-right py-1">Capture Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b">
                      <td className="py-1">14.0 / 74.9</td>
                      <td className="text-right">$1,183,133</td>
                      <td className="text-right">$48.6M</td>
                      <td className="text-right text-gray-600">-$40.2M</td>
                      <td className="text-right text-brand-primary">99.0%</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-1">10.0 / 50.0</td>
                      <td className="text-right">$1,142,567</td>
                      <td className="text-right">$33.3M</td>
                      <td className="text-right text-gray-600">-$25.8M</td>
                      <td className="text-right text-brand-primary">95.7%</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-1">8.0 / 40.0</td>
                      <td className="text-right">$1,098,234</td>
                      <td className="text-right">$26.6M</td>
                      <td className="text-right text-gray-600">-$19.7M</td>
                      <td className="text-right text-brand-primary">92.0%</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-1">5.0 / 25.0</td>
                      <td className="text-right">$892,456</td>
                      <td className="text-right">$16.6M</td>
                      <td className="text-right text-gray-600">-$11.2M</td>
                      <td className="text-right">74.7%</td>
                    </tr>
                    <tr>
                      <td className="py-1">2.0 / 10.0</td>
                      <td className="text-right">$412,789</td>
                      <td className="text-right">$6.7M</td>
                      <td className="text-right text-gray-600">-$4.8M</td>
                      <td className="text-right">34.6%</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};